import { 
  ConversationGenerationRequest, 
  ConversationGenerationResponse 
} from '@/types/conversation';

export interface RequestNewConversationsOptions {
  location?: {
    city?: string;
    state?: string;
    country?: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  weather?: {
    condition: string;
    temperature: number;
    feelsLike: number;
    description: string;
  };
  news?: {
    headlines: Array<{
      title: string;
      category: string;
      source: string;
      timeAgo: string;
    }>;
    trendingTopics: string[];
  };
  preferences?: {
    tone?: 'casual' | 'friendly' | 'professional' | 'lighthearted';
    categories?: string[];
    avoidTopics?: string[];
    includeWeather?: boolean;
    includeNews?: boolean;
    includeLocation?: boolean;
  };
  count?: number;
}

export interface RequestNewConversationsResult {
  success: boolean;
  data?: ConversationGenerationResponse;
  error?: string;
}

export async function requestNewConversations(
  options: RequestNewConversationsOptions = {}
): Promise<RequestNewConversationsResult> {
  try {
    // Prepare the request payload
    const request: ConversationGenerationRequest = {
      location: options.location,
      weather: options.weather,
      news: options.news,
      preferences: {
        tone: 'friendly',
        categories: ['weather', 'news', 'location', 'general'],
        avoidTopics: [],
        includeWeather: true,
        includeNews: true,
        includeLocation: true,
        ...options.preferences
      },
      count: options.count || 5
    };

    // Validate count
    if (request.count && (request.count < 1 || request.count > 10)) {
      return {
        success: false,
        error: 'Count must be between 1 and 10'
      };
    }

    // Make the API call
    const response = await fetch('/.netlify/functions/generateConversationStarters', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`
      };
    }

    const data: ConversationGenerationResponse = await response.json();

    // Validate response structure
    if (!data || !Array.isArray(data.conversations)) {
      return {
        success: false,
        error: 'Invalid response format from conversation generation service'
      };
    }

    return {
      success: true,
      data
    };

  } catch (error) {
    console.error('Error requesting new conversations:', error);
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

export default requestNewConversations;